import {ActiveAccountNumber} from '@lib/accounts/types';
import {MessarioLaunchFragment} from '@lib/messarioLaunch/fragment';
import {
  compareMessarioLaunchUser,
  resolveMessarioLaunchSlot
} from '@lib/messarioLaunch/slots';
import {
  MessarioLaunchMessage,
  MessarioLaunchPendingIntent,
  MessarioLaunchSlot
} from '@lib/messarioLaunch/types';

export type MessarioLaunchAccountLoggedIn = {
  accountNumber: ActiveAccountNumber,
  userId: UserId
};

export type MessarioLaunchCoordinatorDependencies = {
  resolve: (token: string) => Promise<{
    account: {
      panel_user_id: string,
      account_id: string,
      external_user_id: string
    },
    templates_access?: {
      token: string,
      exp: number
    },
    ai_bundle?: {
      enabled: false
    } | {
      enabled: true,
      prompt_revision: number,
      compiled_at: string | null,
      model: string,
      history_limit: number,
      folder_id: number | null,
      features: {
        one_shot_instruction: boolean,
        draft_only: boolean
      },
      variable_definitions_revision?: number
    },
    open_peer?: string | null
  }>,
  readSlots: () => Promise<MessarioLaunchSlot[]>,
  hasSomeonePremium: () => Promise<boolean>,
  currentSlot: () => ActiveAccountNumber,
  changeSlot: (slot: ActiveAccountNumber, options?: {preserveHash?: boolean, hardNavigate?: boolean}) => void,
  openDialog: (peer: string) => void,
  loadPending: () => {pending?: MessarioLaunchPendingIntent, stale: boolean},
  savePending: (pending: MessarioLaunchPendingIntent) => void,
  clearPending: () => void,
  /** Launch token kept across reloads until the resolve settles (see `token.ts`). */
  loadToken: () => {token?: string, stale: boolean},
  clearToken: () => void,
  onAccountLoggedIn: (
    callback: (event: MessarioLaunchAccountLoggedIn) => void
  ) => () => void,
  showMessage: (message: MessarioLaunchMessage) => void,
  now: () => number
};

export class MessarioLaunchCoordinator {
  private pending?: MessarioLaunchPendingIntent;
  private removeLoginListener?: () => void;
  private terminal = false;

  constructor(private dependencies: MessarioLaunchCoordinatorDependencies) {}

  async start(fragment: MessarioLaunchFragment) {
    if(fragment.invalid) {
      this.finish({
        title: 'Messario account launch failed',
        description: 'The Messario launch link is malformed. Return to Messario and click Web again.'
      });
      return;
    }

    // The fragment wins; without it, a token saved by a previous boot of this tab (whose resolve
    // never settled because the page reloaded) is resolved the same way. A stale one is dropped.
    const token = fragment.token || this.dependencies.loadToken().token;

    if(token) {
      this.dependencies.clearPending();

      try {
        const {account, open_peer} = await this.dependencies.resolve(token);
        this.dependencies.clearToken();
        this.pending = {
          expectedUserId: account.external_user_id,
          accountId: account.account_id,
          panelUserId: account.panel_user_id,
          openPeer: normalizeMessarioOpenPeer(open_peer),
          createdAt: this.dependencies.now()
        };
        this.dependencies.savePending(this.pending);
      } catch(err) {
        this.finish({
          title: 'Messario account launch failed',
          description: err instanceof Error ? err.message : 'Could not resolve the Messario launch link.'
        });
        return;
      }
    } else {
      const {pending, stale} = this.dependencies.loadPending();
      if(stale) {
        this.finish({
          title: 'Messario account launch expired',
          description: 'The pending account login is no longer valid. Return to Messario and click Web again.'
        });
        return;
      }

      this.pending = pending;
    }

    if(!this.pending) {
      return;
    }

    this.removeLoginListener = this.dependencies.onAccountLoggedIn((event) => {
      void this.handleLoggedIn(event);
    });

    await this.routeToExpectedAccount();
  }

  private async routeToExpectedAccount() {
    const [slots, hasSomeonePremium] = await Promise.all([
      this.dependencies.readSlots(),
      this.dependencies.hasSomeonePremium()
    ]);
    if(this.terminal) return;

    const resolution = resolveMessarioLaunchSlot(
      slots,
      this.pending.expectedUserId,
      hasSomeonePremium
    );

    if(resolution.kind === 'found') {
      const openPeer = this.pending?.openPeer;
      this.finish();
      if(openPeer) {
        this.dependencies.openDialog(openPeer);
      }
      if(resolution.slot !== this.dependencies.currentSlot()) {
        this.dependencies.changeSlot(resolution.slot);
      }
      return;
    }

    if(resolution.kind === 'full') {
      this.finish({
        title: 'No free Telegram Web K account slot',
        description: 'Telegram Web K allows 3 accounts, or 4 when one is Premium. Log out an account manually, then return to Messario and click Web again.'
      });
      return;
    }

    const selectedSlot = resolution.slot;
    this.pending.selectedSlot = selectedSlot;
    this.dependencies.savePending(this.pending);
    if(selectedSlot !== this.dependencies.currentSlot()) {
      this.dependencies.changeSlot(selectedSlot);
    }
  }

  private async handleLoggedIn(event: MessarioLaunchAccountLoggedIn) {
    if(this.terminal || !this.pending) return;
    if(!this.pending.selectedSlot || event.accountNumber !== this.pending.selectedSlot) return;

    const slots = await this.dependencies.readSlots();
    if(this.terminal) return;

    const loggedInSlot = slots.find(({slot}) => slot === event.accountNumber);
    const actualUserId = loggedInSlot?.userId || String(event.userId);
    if(compareMessarioLaunchUser(this.pending.expectedUserId, actualUserId) === 'match') {
      const openPeer = this.pending.openPeer;
      this.finish();
      if(openPeer) {
        this.dependencies.openDialog(openPeer);
      }
      return;
    }

    this.finish({
      title: 'Different Telegram account signed in',
      description: [
        `Messario expected Telegram user ID ${this.pending.expectedUserId}, but Web K signed in user ID ${actualUserId}.`,
        '',
        'This account was not linked or logged out automatically. Close this tab to cancel, or continue and use Settings → Log Out to remove it manually. Then return to Messario and click Web again.'
      ].join('\n')
    });
  }

  private finish(message?: MessarioLaunchMessage) {
    if(this.terminal) return;

    this.terminal = true;
    this.dependencies.clearPending();
    this.dependencies.clearToken();
    this.removeLoginListener?.();
    if(message) {
      this.dependencies.showMessage(message);
    }
  }
}

export function normalizeMessarioOpenPeer(value: unknown): string | undefined {
  if(typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if(!trimmed) {
    return undefined;
  }

  if(trimmed.startsWith('@')) {
    const username = trimmed.slice(1).replace(/^@+/, '');
    if(!/^[A-Za-z0-9_]{4,32}$/.test(username)) {
      return undefined;
    }
    return `@${username}`;
  }

  if(!/^\d+$/.test(trimmed) || Number(trimmed) <= 0) {
    return undefined;
  }

  return trimmed;
}
